import * as THREE from 'three';

import type { EnvironmentThemeDefinition } from './themes';

export interface CoasterAppearanceDefinition {
  readonly trackColor: number;
  readonly tieColor: number;
  readonly supportColor: number;
  readonly trainColor: number;
  readonly stationColor: number;
  readonly stationRoofColor: number;
}

export const DEFAULT_COASTER_APPEARANCE: CoasterAppearanceDefinition = Object.freeze({
  trackColor: 0x2e5eaa,
  tieColor: 0x283744,
  supportColor: 0x4d5965,
  trainColor: 0xc84e3f,
  stationColor: 0xe9dfcd,
  stationRoofColor: 0x385768,
});

export interface EnvironmentMaterialSet {
  readonly plotTop: THREE.MeshStandardMaterial;
  readonly plotEdge: THREE.MeshStandardMaterial;
  readonly path: THREE.MeshStandardMaterial;
  readonly foliage: THREE.MeshStandardMaterial;
  readonly foliageAccent: THREE.MeshStandardMaterial;
  readonly trunk: THREE.MeshStandardMaterial;
  readonly stone: THREE.MeshStandardMaterial;
  readonly practical: THREE.MeshStandardMaterial;
}

export interface CoasterMaterialSet {
  readonly track: THREE.MeshStandardMaterial;
  readonly tie: THREE.MeshStandardMaterial;
  readonly support: THREE.MeshStandardMaterial;
  readonly train: THREE.MeshStandardMaterial;
  readonly station: THREE.MeshStandardMaterial;
  readonly stationRoof: THREE.MeshStandardMaterial;
  readonly chain: THREE.MeshPhysicalMaterial;
  readonly launch: THREE.MeshStandardMaterial;
  readonly brake: THREE.MeshPhysicalMaterial;
}

function standard(color: number, roughness: number, metalness = 0): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness,
    envMapIntensity: 0.42,
  });
}

function physical(
  color: number,
  roughness: number,
  metalness: number,
  clearcoat: number,
): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color,
    roughness,
    metalness,
    clearcoat,
    clearcoatRoughness: 0.28,
    envMapIntensity: 0.42,
  });
}

export function createEnvironmentMaterials(
  theme: EnvironmentThemeDefinition,
): EnvironmentMaterialSet {
  return {
    plotTop: standard(theme.colors.plotTop, theme.surface.groundRoughness),
    plotEdge: standard(theme.colors.plotEdge, 0.72, 0.05),
    path: standard(theme.colors.path, theme.surface.stoneRoughness),
    foliage: standard(theme.colors.foliage, theme.surface.sceneryRoughness),
    foliageAccent: standard(theme.colors.foliageAccent, theme.surface.sceneryRoughness),
    trunk: standard(theme.colors.trunk, 0.88),
    stone: standard(theme.colors.stone, theme.surface.stoneRoughness),
    practical: new THREE.MeshStandardMaterial({
      color: theme.colors.practical,
      emissive: theme.colors.practical,
      emissiveIntensity: theme.id === 'afterglow' ? 2.2 : 0.3,
      roughness: 0.4,
      envMapIntensity: 0.35,
    }),
  };
}

export function createCoasterMaterials(
  appearance: CoasterAppearanceDefinition = DEFAULT_COASTER_APPEARANCE,
): CoasterMaterialSet {
  return {
    // Restore the Phase 0 painted-steel response: the clear coat provides the
    // readable highlight rolloff that was lost when Phase 1 flattened these
    // slots to MeshStandardMaterial.
    track: physical(appearance.trackColor, 0.28, 0.58, 0.26),
    tie: physical(appearance.tieColor, 0.38, 0.62, 0.12),
    support: physical(appearance.supportColor, 0.46, 0.52, 0.08),
    train: physical(appearance.trainColor, 0.26, 0.2, 0.55),
    station: standard(appearance.stationColor, 0.82),
    stationRoof: physical(appearance.stationRoofColor, 0.42, 0.22, 0.34),
    chain: new THREE.MeshPhysicalMaterial({
      color: 0xe1b84b,
      emissive: 0x5a3b05,
      emissiveIntensity: 0.28,
      roughness: 0.3,
      metalness: 0.72,
      clearcoat: 0.18,
      clearcoatRoughness: 0.28,
      envMapIntensity: 0.48,
    }),
    launch: new THREE.MeshStandardMaterial({
      color: 0x62def2,
      emissive: 0x38bcd8,
      emissiveIntensity: 0.75,
      roughness: 0.32,
      metalness: 0.5,
      envMapIntensity: 0.42,
    }),
    brake: new THREE.MeshPhysicalMaterial({
      color: 0xd96f35,
      emissive: 0x4a1607,
      emissiveIntensity: 0.18,
      roughness: 0.36,
      metalness: 0.58,
      clearcoat: 0.16,
      clearcoatRoughness: 0.3,
      envMapIntensity: 0.46,
    }),
  };
}

export function disposeMaterialSet(materials: object): void {
  for (const material of Object.values(materials) as THREE.Material[]) material.dispose();
}
